import { useState } from "react";
import { Eye, EyeOff, Check, Loader2, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { HELP_TEXT, ROLE_TABS } from "@/lib/roles";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/GoogleAuth";
import { CantLoginDialog } from "@/components/CantLogin";

const Field = ({ label, accent, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-bold tracking-[2px]" style={{ color: accent }}>{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-lg border border-[#333] bg-[#121212] px-4 py-3.5 text-[15px] text-white placeholder:text-neutral-500 outline-none transition-colors focus:border-[var(--accent)]";

export const LoginForm = ({ role, accent }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [pendingMsg, setPendingMsg] = useState(null);
  const [f, setF] = useState({ email: "", password: "", full_name: "", company: "", phone: "", use_case: "" });
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const meta = ROLE_TABS.find((x) => x.key === role);
  const isSignup = role === "machine_owner" && mode === "signup";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) return setError("Please acknowledge the confidentiality agreement to continue.");
    setBusy(true);
    setError(null);
    try {
      if (isSignup) {
        if (f.full_name.trim().length < 2) throw new Error("Please enter your full name.");
        if (f.password.length < 8) throw new Error("Password must be at least 8 characters.");
        const res = await api("/auth/signup/machine-owner", {
          method: "POST",
          auth: false,
          body: {
            email: f.email.trim(), password: f.password, full_name: f.full_name.trim(),
            company: f.company.trim() || undefined, phone: f.phone.trim() || undefined, use_case: f.use_case.trim() || undefined,
          },
        });
        setPendingMsg(res.message);
      } else {
        await login(f.email.trim(), f.password, role);
      }
    } catch (err) {
      setError(err.message || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  if (pendingMsg) return (
    <div className="rounded-xl border p-5 text-[14px] leading-6 text-neutral-200" style={{ borderColor: accent, background: `${accent}14` }} data-testid="signup-pending-msg">
      <p className="mb-1 text-[11px] font-bold tracking-[3px]" style={{ color: accent }}>ACCOUNT CREATED</p>
      {pendingMsg}
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#333] bg-[#1A1A1A]/95 p-6 backdrop-blur-xl" style={{ "--accent": accent }} data-testid="login-form">
      {role === "machine_owner" && (
        <div className="mb-5 flex rounded-lg border border-[#333] bg-[#121212] p-1">
          {[["signup", "CREATE ACCOUNT"], ["signin", "I HAVE AN ACCOUNT"]].map(([k, label]) => (
            <button key={k} type="button" data-testid={`prospect-mode-${k}`} onClick={() => { setMode(k); setError(null); }}
              className="flex-1 rounded-md py-2.5 text-[11px] font-extrabold tracking-[0.12em] transition-colors"
              style={mode === k ? { background: accent, color: "#000" } : { color: "#A0A0A0" }}>
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {isSignup && (
          <>
            <Field label="FULL NAME" accent={accent}><input data-testid="signup-name" className={inputCls} value={f.full_name} onChange={set("full_name")} placeholder="Jane Doe" /></Field>
            <Field label="COMPANY (OPTIONAL)" accent={accent}><input data-testid="signup-company" className={inputCls} value={f.company} onChange={set("company")} placeholder="Doe Restoration LLC" /></Field>
            <Field label="PHONE (OPTIONAL)" accent={accent}><input data-testid="signup-phone" className={inputCls} value={f.phone} onChange={set("phone")} placeholder="(555) 555-5555" type="tel" /></Field>
          </>
        )}
        <Field label="EMAIL" accent={accent}>
          <input data-testid="login-email" type="email" autoComplete="email" required className={inputCls} value={f.email} onChange={set("email")} placeholder="you@alrclean.com" />
        </Field>
        <Field label="PASSWORD" accent={accent}>
          <div className="relative">
            <input data-testid="login-password" type={showPw ? "text" : "password"} autoComplete={isSignup ? "new-password" : "current-password"} required
              className={`${inputCls} pr-12`} value={f.password} onChange={set("password")} placeholder={isSignup ? "Min. 8 characters" : "••••••••"} />
            <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-neutral-400 hover:text-white">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Field>
        {isSignup && (
          <Field label="WHAT DO YOU WANT TO CLEAN? (OPTIONAL)" accent={accent}>
            <textarea data-testid="signup-usecase" rows={3} className={inputCls} value={f.use_case} onChange={set("use_case")} placeholder="e.g. rust on antique car parts, mold from boat hulls" />
          </Field>
        )}
      </div>

      {!isSignup && (
        <div className="mt-2 flex items-center justify-between gap-4">
          <button type="button" onClick={() => setHelpOpen(true)} data-testid="cant-login-link" className="py-2 text-[13px] font-bold underline" style={{ color: accent }}>Can't log in?</button>
          <Link to="/forgot-password" data-testid="forgot-password-link" className="py-2 text-[13px] font-bold underline" style={{ color: accent }}>Forgot password?</Link>
        </div>
      )}

      {error && <p data-testid="login-error" className="mt-3 text-[13px] text-[#FF3333]">{error}</p>}

      <button type="button" data-testid="confidentiality-checkbox" onClick={() => setAgreed((v) => !v)}
        className="mt-4 flex w-full items-start gap-3 rounded-lg border border-[#FFC107]/55 bg-[#FFC107]/[0.07] p-3 text-left">
        <span className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded border-2" style={{ borderColor: accent, background: agreed ? accent : "transparent" }}>
          {agreed && <Check size={14} color="#000" strokeWidth={3} />}
        </span>
        <span className="text-[11px] leading-4 text-white">
          <b style={{ color: accent }}>CONFIDENTIAL — </b>
          I agree that all information, documents, AI conversations, training materials, pricing, images, and any other content within this app are strictly confidential and the property of ALR. I will not share, copy, reproduce, photograph, screen-record, or otherwise disclose any portion of this app to anyone outside of authorized ALR personnel.
        </span>
      </button>

      <button type="submit" data-testid="login-submit" disabled={busy || !agreed}
        className="mt-5 flex w-full items-center justify-center rounded-lg py-4 text-[13px] font-extrabold tracking-[1.6px] text-black transition-opacity duration-150 disabled:opacity-50"
        style={{ background: accent }}>
        {busy ? <Loader2 className="animate-spin" size={18} /> : isSignup ? "CREATE MY ACCOUNT" : `SIGN IN AS ${meta.label.toUpperCase()}`}
      </button>

      {role !== "prospect" && (
        <>
          <div className="mt-4 flex items-center gap-3 text-[10px] font-bold tracking-[0.2em] text-neutral-500"><span className="h-px flex-1 bg-[#333]" />OR<span className="h-px flex-1 bg-[#333]" /></div>
          <GoogleButton role={role} disabled={!agreed} onBlocked={() => setError("Please acknowledge the confidentiality agreement to continue.")} />
        </>
      )}

      {role === "rental_customer" && (
        <div className="mt-5">
          <div className="mb-3 flex items-center gap-3 text-[10px] font-bold tracking-[0.2em] text-neutral-500">
            <span className="h-px flex-1 bg-[#333]" />NEW HERE?<span className="h-px flex-1 bg-[#333]" />
          </div>
          <button type="button" data-testid="signup-rental-link" onClick={() => navigate("/signup-rental")}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 py-3.5 text-[13px] font-black tracking-wider"
            style={{ borderColor: accent, color: accent, background: `${accent}14` }}>
            <UserPlus size={18} /> CREATE RENTAL CUSTOMER ACCOUNT
          </button>
          <p className="mt-2 text-center text-[11px] text-neutral-500">Sign up in 60 seconds · Verify email · $30 LSO Training</p>
        </div>
      )}

      <p className="mt-4 text-[11px] leading-4 text-neutral-400" data-testid="login-help-text">
        {isSignup ? "New machine-owner accounts are reviewed by ALR. You'll get an email as soon as yours is approved." : HELP_TEXT[role]}
      </p>
      {helpOpen && <CantLoginDialog open={helpOpen} onClose={() => setHelpOpen(false)} accent={accent} initialEmail={f.email} />}
    </form>
  );
};
